<template>
  <div class="full-screen-background">
  <div class="rank-container">
    <!-- Sidebar para desktop -->
    <aside class="rank-sidebar">
      <!-- botões de tipo de rank -->
      <button
        v-for="cfg in ranksConfig" 
        :key="cfg.key"
        :class="{ active: cfg.key === selectedRank }"
        @click="selectRank(cfg.key)"
      >
        {{ t(`rank.ranks.${cfg.key}`) }}
      </button>

      <!-- dropdown de englobamentos -->
      <div class="group-select " >
        <label for="groupSelect">{{ t('rank.selectGroupLabel') }}</label>
        <select 
          id="groupSelect"
          v-model="selectedGroup"
          @change="onGroupChange"
        >
          <option value="">{{ t('rank.none') }}</option>
          <option
            v-for="(classes, group) in classesByCategory"
            :key="group"
            :value="group"
          >
            {{ group }}
          </option>
        </select>
      </div>
    </aside>

    <section class="rank-content">
      <!-- Botão de filtros para mobile -->
      <button style="background-color: #ff5252;" class="filter-toggle" @click="isFilterOpen = !isFilterOpen">
        Filtre aqui
        <span v-if="!isFilterOpen">▾</span>
        <span v-else>▴</span>
      </button>

      <!-- Painel de filtros (aparece só em mobile) -->
      <div class="filter-panel" v-if="isFilterOpen">
        <button
          v-for="cfg in ranksConfig"
          :key="cfg.key"
          :class="{ active: cfg.key === selectedRank }"
          @click="selectRank(cfg.key)"
        >
          {{ t(`rank.ranks.${cfg.key}`) }}
        </button>
        <div class="group-select">
          <label for="groupSelectMobile">{{ t('rank.selectGroupLabel') }}</label>
          <select
            id="groupSelectMobile"
            v-model="selectedGroup"
            @change="onGroupChange"
          >
            <option value="">{{ t('rank.none') }}</option>
            <option
              v-for="(classes, group) in classesByCategory"
              :key="group"
              :value="group"
            >
              {{ group }}
            </option>
          </select>
        </div>
      </div>

      <!-- título do rank -->
      <h2>{{ currentLabel }}</h2>

      <!-- sugestão de orientação -->
      <p class="orientation-note">
        Para melhor visualização, utilize seu dispositivo na orientação horizontal.
      </p>

      <!-- descrição das classes do grupo -->
      <p v-if="selectedGroup" class="group-description">
        {{ selectedGroup }}:
        <strong>{{ groupClassNames }}</strong>
      </p>

      <transition name="slide" mode="out-in">
        <div v-if="!loading" :key="tableKey" class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>{{ t('rank.position') }}</th>
                <th>{{ t('rank.name') }}</th>
                <th>{{ t('rank.level') }}</th>
                <th>{{ t('rank.class') }}</th>
                <th>{{ statLabel }}</th>
                <th>{{ t('rank.rebirth') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(p, index) in filteredData"
                :key="p.player_name"
                :class="{
                  gold:   index === 0,
                  silver: index === 1,
                  bronze: index === 2
                }"
              >
                <td class="position">
                  <span v-if="index === 0">🥇</span>
                  <span v-else-if="index === 1">🥈</span>
                  <span v-else-if="index === 2">🥉</span>
                  <span v-else>{{ index + 1 }}</span>
                </td>
                <td>{{ p.player_name }}</td>
                <td>{{ p.player_lv }}</td>
                <td>{{ classMap[p.player_class] || '—' }}</td>
                <td>{{ p[statKey] }}</td>
                <td>
                  {{ p.rebirth_count === 1
                     ? t('rank.reincarnated')
                     : t('rank.notReincarnated') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else key="loading">{{ t('rank.loading') }}</p>
      </transition>
    </section>
  </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import classesData       from './jsonClasses/classes.json'
import classesByCategory from './jsonClasses/classesByCategory.json'
import {
  fetchRankDeaths,
  fetchRankCrystalDamage,
  fetchRankHeal,
  fetchRankPlayerDamage,
  fetchRankWins
} from '@/services/api'

const { t } = useI18n()

// configuração estática dos tipos de rank
const ranksConfig = [
  { key: 'deaths',        fn: fetchRankDeaths,        statKey: 'kill_count' },
  { key: 'crystalDamage', fn: fetchRankCrystalDamage, statKey: 'hight_altar_damage' },
  { key: 'heal',          fn: fetchRankHeal,          statKey: 'hight_cure' },
  { key: 'playerDamage',  fn: fetchRankPlayerDamage,  statKey: 'hight_damage' },
  { key: 'wins',          fn: fetchRankWins,          statKey: 'win_count' }
]

const selectedRank   = ref('deaths')
const rankData       = ref([])
const loading        = ref(false)
const selectedGroup  = ref('')
const isFilterOpen   = ref(false)

// referências estáticas
const classesByCategoryRef = classesByCategory
const classesDataRef       = classesData

// computed helpers
const classMap = computed(() =>
  classesDataRef.reduce((map, c) => {
    map[c.id] = c.name
    return map
  }, {})
)

const currentConfig = computed(() =>
  ranksConfig.find(r => r.key === selectedRank.value)
)

const statKey       = computed(() => currentConfig.value.statKey)
const currentLabel  = computed(() =>
  t(`rank.ranks.${selectedRank.value}`)
)
const statLabel     = computed(() =>
  t(`rank.stat.${selectedRank.value}`)
)

const filteredData = computed(() => {
  let data = [...rankData.value]
  if (selectedGroup.value) {
    const ids = classesByCategoryRef[selectedGroup.value].map(c => c.id)
    data = data.filter(p => ids.includes(p.player_class))
  }
  return data.sort((a, b) => {
    if (b[statKey.value] !== a[statKey.value]) {
      return b[statKey.value] - a[statKey.value]
    }
    return b.player_lv - a.player_lv
  })
})

const groupClasses = computed(() =>
  selectedGroup.value
    ? classesByCategoryRef[selectedGroup.value]
    : []
)
const groupClassNames = computed(() =>
  groupClasses.value.map(c => c.name).join(', ')
)

const tableKey = computed(() =>
  `${selectedRank.value}-${selectedGroup.value || 'all'}`
)

// métodos
async function selectRank(key) {
  if (key === selectedRank.value) return
  selectedRank.value = key
  loading.value = true
  try {
    const token = localStorage.getItem('token')
    rankData.value = await currentConfig.value.fn(token)
  } catch (err) {
    console.error(err)
    rankData.value = []
  } finally {
    loading.value = false
  }
}

function onGroupChange() {
  // o computed filteredData já reage
}

onMounted(() => {
  selectRank(selectedRank.value)
})
</script>

<style scoped>


.full-screen-background {
  background-image: url('@/assets/background/fundotrocasenha.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  min-height: 100vh;
  width: 100%;
  position: relative;
}

.rank-container {
  display: flex;
  height: 100%;
  
}

/* === Desktop === */
.rank-sidebar {
  width: 240px;
  padding: 1rem;
  background: #11111113;
  color: #ffffff;
}
.rank-sidebar button {
  display: block;
  width: 100%;
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.rank-sidebar button.active {
  font-weight: bold;
}
.group-select {
  margin-top: 2rem;
  color: #ffffff;

}
.group-select label {
  display: block;
  margin-bottom: 0.25rem;
  font-weight: bold;
  color: #ffffff;
}
.group-select select {

  width: 100%;
  padding: 0.5rem;
  color: #ff0000;
  text-decoration: bound;
}

/* === Filtros mobile === */
.filter-toggle {
  display: none;
  margin-bottom: 1rem;
  background: #007bff;
  color: #fff;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  width: 100%;
  text-align: left;
  font-size: 1rem;
  cursor: pointer;
}
.filter-panel {
  padding: 1rem;
  background: #f0f0f0;
  margin-bottom: 1rem;
}
.filter-panel button {
  display: block;
  width: 100%;
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.filter-panel button.active {
  font-weight: bold;
}

/* Conteúdo principal */
.rank-content {
  flex: 1;
  padding: 1rem;
}
.orientation-note {
  font-style: italic;
  margin: 0.5rem 0 1rem;
}
.group-description {
  margin: 0.5rem 0 1rem;
  font-style: italic;
}

/* wrapper que permite scroll horizontal */
.table-wrapper {
  overflow-x: auto;
}

/* tabela sem width fixa, com layout natural */
table {
  border-collapse: collapse;
  table-layout: auto;
  width: auto;
  min-width: 100%;
}
th, td {
  padding: 0.5rem;
  border: 1px solid #ddd;
  white-space: nowrap;
  text-align: left;
}
.position {
  width: 2rem;
}
.gold   { background: rgba(255, 215, 0, 0.2); }
.silver { background: rgba(192, 192, 192, 0.2); }
.bronze { background: rgba(205, 127, 50, 0.2); }

/* transição slide */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.4s ease;
}
.slide-enter {
  transform: translateX(-100%);
  opacity: 0;
}
.slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* === Responsivo === */
@media (max-width: 767px) {
  .rank-sidebar {
    display: none;
  }
  .filter-toggle {
    display: block;
  }
}

/* === Dark Mode === */
/* @media (prefers-color-scheme: dark) {
  .rank-sidebar { background-color: #000;
     color: #fff; 
    }
  .rank-sidebar button { color: inherit; }
  .group-select select,
  .group-select select option {
    background-color: #333;
    color: #fff;
    border: 1px solid #555;
  }
  .filter-panel { background: #222; }
  .filter-toggle { background: #0056b3; }
} */
</style>
